import React, {
  createContext,
  Fragment,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
} from "react";

type ModalId = string;

export type ManagedModalComponentProps = {
  open: boolean;
  onClose: () => void;
  ref?: React.Ref<HTMLElement>;
};

export type ImperativeModalRenderer = (
  managedProps: ManagedModalComponentProps
) => React.ReactElement;

type ImperativeModalEntry = {
  render: ImperativeModalRenderer;
  onClose: () => void;
};

interface ModalManagerState {
  registeredIds: ModalId[];
  stack: ModalId[];
  imperativeModals: Record<ModalId, ImperativeModalEntry>;
}

type ModalAction =
  | { type: "REGISTER"; payload: { id: ModalId } }
  | { type: "UNREGISTER"; payload: { id: ModalId } }
  | { type: "OPEN"; payload: { id: ModalId } }
  | { type: "CLOSE"; payload: { id: ModalId } }
  | { type: "CLOSE_ALL" }
  | { type: "SET_IMPERATIVE"; payload: { id: ModalId; entry: ImperativeModalEntry } }
  | { type: "CLEAR_IMPERATIVE"; payload: { id: ModalId } };

const modalReducer = (state: ModalManagerState, action: ModalAction): ModalManagerState => {
  switch (action.type) {
    case "REGISTER": {
      const { id } = action.payload;
      if (state.registeredIds.includes(id)) {
        return state;
      }
      return { ...state, registeredIds: [...state.registeredIds, id] };
    }
    case "UNREGISTER": {
      const { id } = action.payload;
      return {
        ...state,
        registeredIds: state.registeredIds.filter((registeredId) => registeredId !== id),
      };
    }
    case "OPEN": {
      if (
        !state.registeredIds.includes(action.payload.id) ||
        state.stack.includes(action.payload.id)
      ) {
        return state;
      }
      return { ...state, stack: [...state.stack, action.payload.id] };
    }
    case "CLOSE": {
      if (!state.stack.includes(action.payload.id)) return state;
      return { ...state, stack: state.stack.filter((id) => id !== action.payload.id) };
    }
    case "CLOSE_ALL": {
      if (state.stack.length === 0) return state;
      return { ...state, stack: [] };
    }
    case "SET_IMPERATIVE": {
      const { id, entry } = action.payload;
      return {
        ...state,
        imperativeModals: {
          ...state.imperativeModals,
          [id]: entry,
        },
      };
    }
    case "CLEAR_IMPERATIVE": {
      const { id } = action.payload;
      if (!state.imperativeModals[id]) return state;
      const { [id]: _removed, ...remaining } = state.imperativeModals;
      return {
        ...state,
        imperativeModals: remaining,
      };
    }
    default:
      return state;
  }
};

interface ModalContextType {
  modalStack: ModalId[];
  isModalOpen: (payload: { id: ModalId }) => boolean;
  open: (payload: { id: ModalId }) => void;
  close: (payload: { id: ModalId }) => void;
  closeAll: () => void;
  register: (payload: { id: ModalId }) => void;
  unregister: (payload: { id: ModalId }) => void;
  focus: (payload: { id: ModalId }) => void;
  setModalRef: (payload: { id: ModalId; el: HTMLElement | null }) => void;
  setImperativeModal: (payload: { id: ModalId; entry: ImperativeModalEntry }) => void;
  clearImperativeModal: (payload: { id: ModalId }) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [state, dispatch] = useReducer(modalReducer, {
    registeredIds: [],
    stack: [],
    imperativeModals: {},
  });
  const modalRefs = useRef<Map<ModalId, HTMLElement>>(new Map());

  const register = useCallback<ModalContextType["register"]>(
    ({ id }) => dispatch({ type: "REGISTER", payload: { id } }),
    []
  );
  const unregister = useCallback<ModalContextType["unregister"]>(({ id }) => {
    dispatch({ type: "UNREGISTER", payload: { id } });
    modalRefs.current.delete(id);
  }, []);
  const open = useCallback<ModalContextType["open"]>(
    ({ id }) => dispatch({ type: "OPEN", payload: { id } }),
    []
  );
  const close = useCallback<ModalContextType["close"]>(
    ({ id }) => dispatch({ type: "CLOSE", payload: { id } }),
    []
  );
  const closeAll = useCallback<ModalContextType["closeAll"]>(
    () => dispatch({ type: "CLOSE_ALL" }),
    []
  );
  const setImperativeModal = useCallback<ModalContextType["setImperativeModal"]>(
    ({ id, entry }) => dispatch({ type: "SET_IMPERATIVE", payload: { id, entry } }),
    []
  );
  const clearImperativeModal = useCallback<ModalContextType["clearImperativeModal"]>(
    ({ id }) => dispatch({ type: "CLEAR_IMPERATIVE", payload: { id } }),
    []
  );

  const isModalOpen = useCallback<ModalContextType["isModalOpen"]>(
    ({ id }) => state.stack.includes(id),
    [state.stack]
  );
  const modalStack = state.stack;
  const imperativeModals = state.imperativeModals;

  const focus = useCallback<ModalContextType["focus"]>(({ id }) => {
    const modalElement = modalRefs.current.get(id);
    if (modalElement) {
      queueMicrotask(() => modalElement.focus());
    }
  }, []);

  const setModalRef = useCallback<ModalContextType["setModalRef"]>(({ id, el }) => {
    if (el) {
      modalRefs.current.set(id, el);
    } else {
      modalRefs.current.delete(id);
    }
  }, []);

  useEffect(() => {
    if (modalStack.length > 0) {
      const topModalId = modalStack[modalStack.length - 1];
      const topModalRef = modalRefs.current.get(topModalId);
      if (topModalRef && !topModalRef.contains(document.activeElement)) {
        focus({ id: topModalId });
      }
    }
  }, [modalStack, focus]);

  const imperativeModalIds = useMemo(() => {
    const ids = Object.keys(imperativeModals);
    if (ids.length === 0) return [];
    const stackSet = new Set(modalStack);
    const stackIds = modalStack.filter((id) => imperativeModals[id]);
    const remainingIds = ids.filter((id) => !stackSet.has(id));
    return [...stackIds, ...remainingIds];
  }, [imperativeModals, modalStack]);

  const imperativeModalNodes = useMemo(() => {
    if (imperativeModalIds.length === 0) return null;
    return imperativeModalIds.map((id) => {
      const entry = imperativeModals[id];
      if (!entry) return null;
      const isOpen = modalStack.includes(id);
      const handleClose = () => {
        entry.onClose();
      };

      const managedProps: ManagedModalComponentProps = {
        open: isOpen,
        onClose: handleClose,
        ref: (el: HTMLElement | null) => setModalRef({ id, el }),
      };

      return <Fragment key={id}>{entry.render(managedProps)}</Fragment>;
    });
  }, [imperativeModalIds, imperativeModals, modalStack, setModalRef]);

  const contextValue = useMemo<ModalContextType>(
    () => ({
      modalStack,
      isModalOpen,
      open,
      close,
      closeAll,
      register,
      unregister,
      focus,
      setModalRef,
      setImperativeModal,
      clearImperativeModal,
    }),
    [
      modalStack,
      isModalOpen,
      open,
      close,
      closeAll,
      register,
      unregister,
      focus,
      setModalRef,
      setImperativeModal,
      clearImperativeModal,
    ]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {imperativeModalNodes}
    </ModalContext.Provider>
  );
};

export const useModalManager = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalManager must be used within a ModalProvider");
  }
  return context;
};

interface UseManagedModalOptions {
  id?: string;
  onClose?: () => void;
}

interface UseImperativeModalOptions {
  id?: string;
  onClose?: () => void;
}

export interface ManagedModal {
  props: ManagedModalComponentProps;
  open: () => void;
  close: () => void;
  isOpen: boolean;
  id: ModalId;
}

export interface ImperativeModal {
  open: (render: ImperativeModalRenderer) => void;
  close: () => void;
  isOpen: boolean;
  id: ModalId;
}

export const useManagedModal = (options: UseManagedModalOptions = {}): ManagedModal => {
  const context = useModalManager();
  const generatedId = useId();
  const id = options.id ?? generatedId;

  useEffect(() => {
    context.register({ id });
    return () => context.unregister({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, context.register, context.unregister]);

  const onCloseRef = useRef(options.onClose);
  useEffect(() => {
    onCloseRef.current = options.onClose;
  });

  const open = useCallback(() => context.open({ id }), [context, id]);

  const close = useCallback(() => {
    context.close({ id });
    onCloseRef.current?.();
  }, [context, id]);

  const isOpen = context.isModalOpen({ id });

  const refCallback = useCallback(
    (el: HTMLElement | null) => {
      context.setModalRef({ id, el });
    },
    [context, id]
  );

  const modalProps = useMemo<ManagedModalComponentProps>(
    () => ({
      open: isOpen,
      onClose: close,
      ref: refCallback,
    }),
    [isOpen, close, refCallback]
  );

  return {
    id,
    props: modalProps,
    open,
    close,
    isOpen,
  };
};

export const useImperativeModal = (options: UseImperativeModalOptions = {}): ImperativeModal => {
  const context = useModalManager();
  const generatedId = useId();
  const id = options.id ?? generatedId;

  useEffect(() => {
    context.register({ id });
    return () => {
      context.close({ id });
      context.clearImperativeModal({ id });
      context.unregister({ id });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, context.register, context.unregister, context.clearImperativeModal, context.close]);

  const onCloseRef = useRef(options.onClose);
  useEffect(() => {
    onCloseRef.current = options.onClose;
  });

  const close = useCallback(() => {
    context.close({ id });
    onCloseRef.current?.();
  }, [context, id]);

  const open = useCallback(
    (render: ImperativeModalRenderer) => {
      context.register({ id });
      context.setImperativeModal({
        id,
        entry: {
          render,
          onClose: close,
        },
      });
      context.open({ id });
    },
    [context, id, close]
  );

  const isOpen = context.isModalOpen({ id });

  return {
    id,
    open,
    close,
    isOpen,
  };
};
